import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api, { errMsg } from "../api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import NoteRichEditor from "../components/NoteRichEditor";
import DropZone from "../components/DropZone";
import {
    Inbox, Send as SendIcon, FileText, Trash2, Archive, AlertTriangle, Folder,
    Mail, RefreshCw, Search, Sparkles, ListPlus, Reply, ReplyAll, Forward, X,
    Paperclip, Download, ChevronDown, ChevronRight, Edit3, Loader2, CheckCircle2,
    Pen, Plus, FileSignature, Flag, FolderPlus, Inbox as InboxIcon, AtSign,
    MailOpen, Layers
} from "lucide-react";

/**
 * MailPage — Premium Outlook-style mail client embedded in MyTACS.
 * Layout: Outlook-tree sidebar (per-mailbox folders + unified inbox) | Message list | Detail.
 */

// Folder icons (language-agnostic, includes DE + EN)
const FOLDER_ICONS = {
    inbox: Inbox, posteingang: Inbox,
    drafts: FileText, "entwürfe": FileText, entwurf: FileText,
    sent: SendIcon, "sent items": SendIcon, "gesendete elemente": SendIcon, gesendet: SendIcon,
    "junk email": AlertTriangle, "junk e-mail": AlertTriangle, "junk-e-mail": AlertTriangle, junk: AlertTriangle, spam: AlertTriangle,
    "deleted items": Trash2, "gelöschte elemente": Trash2, papierkorb: Trash2, trash: Trash2,
    archive: Archive, archiv: Archive,
    outbox: SendIcon, postausgang: SendIcon,
    erledigt: CheckCircle2, done: CheckCircle2,
};

function folderIconFor(name = "") {
    return FOLDER_ICONS[name.toLowerCase()] || Folder;
}

function findPrimaryFolder(folders) {
    if (!folders?.length) return null;
    return folders.find((f) => {
        const n = (f.displayName || "").toLowerCase();
        return n === "inbox" || n === "posteingang";
    }) || folders[0];
}

function fmtDateTime(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString(lang === "de" ? "de-DE" : "en-US", { hour: "2-digit", minute: "2-digit" });
    if (isToday) return time;
    if (isYesterday) return `${lang === "de" ? "Gestern" : "Yesterday"} · ${time}`;
    const date = d.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", { day: "2-digit", month: "short" });
    return `${date} · ${time}`;
}

function fmtFullDateTime(iso, lang) {
    if (!iso) return "";
    return new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function initialsFor(name = "") {
    return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "?";
}

function avatarColor(seed = "") {
    const palette = ["#2563eb", "#db2777", "#9333ea", "#0ea5e9", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2"];
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
}

export default function MailPage() {
    const { lang } = useAuth();
    const location = useLocation();
    const t = (de, en) => (lang === "de" ? de : en);

    const [mailboxes, setMailboxes] = useState([]);
    const [activeMailbox, setActiveMailbox] = useState("me");
    const [foldersByMailbox, setFoldersByMailbox] = useState({});
    const [expandedMailboxes, setExpandedMailboxes] = useState({});
    const [activeFolderId, setActiveFolderId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [activeMsgId, setActiveMsgId] = useState(null);
    const [activeMsg, setActiveMsg] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [search, setSearch] = useState("");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [composeOpen, setComposeOpen] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiSummary, setAiSummary] = useState(null);
    const [messageAssignee, setMessageAssignee] = useState(null);  // { has_task, claimer, assignees, status, auto_assigned_by_pool }
    const [newFolderFor, setNewFolderFor] = useState(null); // mailbox id or null
    const [newFolderName, setNewFolderName] = useState("");
    const [dragKey, setDragKey] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);

    const folders = useMemo(() => foldersByMailbox[activeMailbox] || [], [foldersByMailbox, activeMailbox]);

    // ----- Loaders -----
    const loadMailboxes = useCallback(async () => {
        try {
            const { data } = await api.get("/ms/mail/mailboxes");
            setMailboxes(data || []);
            // Expand the first mailbox by default
            if (data?.length) {
                setExpandedMailboxes((prev) => ({ ...prev, [data[0].id]: true }));
            }
        } catch (e) { toast.error(errMsg(e)); }
    }, []);

    const loadFoldersFor = useCallback(async (mbId) => {
        if (!mbId || mbId === "__all__") return;
        try {
            const { data } = await api.get(`/ms/mail/folders?mailbox=${encodeURIComponent(mbId)}`);
            setFoldersByMailbox((prev) => ({ ...prev, [mbId]: data || [] }));
            return data || [];
        } catch (e) { /* silent — sidebar shows mailbox without folders */ }
    }, []);

    const loadMessages = useCallback(async () => {
        if (!activeMailbox) return;
        setLoading(true);
        try {
            if (activeMailbox === "__all__") {
                const { data } = await api.get("/ms/mail/messages/all?top=30");
                setMessages(data || []);
            } else {
                const params = new URLSearchParams({ mailbox: activeMailbox, top: "50" });
                if (activeFolderId) params.set("folder_id", activeFolderId);
                if (search.trim()) params.set("search", search.trim());
                else if (unreadOnly) params.set("unread_only", "true");
                const { data } = await api.get(`/ms/mail/messages?${params.toString()}`);
                setMessages(data || []);
            }
        } catch (e) { toast.error(errMsg(e)); }
        finally { setLoading(false); }
    }, [activeMailbox, activeFolderId, search, unreadOnly]);

    const loadMessage = useCallback(async (msgId, sourceMailbox) => {
        if (!msgId) return;
        const mb = sourceMailbox || (activeMailbox === "__all__" ? null : activeMailbox);
        if (!mb) return;
        setActiveMsg(null);
        setAiSummary(null);
        setMessageAssignee(null);
        try {
            const [m, atts, ass] = await Promise.all([
                api.get(`/ms/mail/messages/${msgId}?mailbox=${encodeURIComponent(mb)}`),
                api.get(`/ms/mail/messages/${msgId}/attachments?mailbox=${encodeURIComponent(mb)}`),
                api.get(`/ms/mail/messages/${msgId}/assignee?mailbox=${encodeURIComponent(mb)}`).catch(() => ({ data: null })),
            ]);
            setActiveMsg({ ...m.data, _mailbox_id: mb });
            setAttachments((atts.data || []).filter((a) => !a.isInline));
            setMessageAssignee(ass.data || null);
            setMessages((prev) => prev.map((p) => p.id === msgId ? { ...p, isRead: true } : p));
        } catch (e) { toast.error(errMsg(e)); }
    }, [activeMailbox]);

    // ----- Initial + reactive loads -----
    useEffect(() => { loadMailboxes(); }, [loadMailboxes]);

    // Detect ?msg=&mailbox= URL params and pre-open that mail (deep link from Task claim).
    // We depend on location.search so this re-fires whenever the URL changes —
    // important because TabPagesRouter keeps the MailPage mounted (no re-mount on nav).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const msgParam = params.get("msg");
        const mbParam = params.get("mailbox");
        if (!msgParam) return;
        const targetMb = mbParam || "me";
        setActiveMailbox(targetMb);
        (async () => {
            try {
                const [m, atts] = await Promise.all([
                    api.get(`/ms/mail/messages/${msgParam}?mailbox=${encodeURIComponent(targetMb)}`),
                    api.get(`/ms/mail/messages/${msgParam}/attachments?mailbox=${encodeURIComponent(targetMb)}`),
                ]);
                setActiveMsg({ ...m.data, _mailbox_id: targetMb });
                setAttachments((atts.data || []).filter((a) => !a.isInline));
                setActiveMsgId(msgParam);
            } catch (e) { toast.error(errMsg(e)); }
        })();
        // Clean URL so a manual refresh doesn't keep re-opening
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
    }, [location.search]);

    // When mailboxes load, fetch folders for each (parallel)
    useEffect(() => {
        mailboxes.forEach((mb) => {
            if (!foldersByMailbox[mb.id]) loadFoldersFor(mb.id);
        });
    }, [mailboxes, foldersByMailbox, loadFoldersFor]);

    // When activeMailbox changes, pick a sensible activeFolder
    useEffect(() => {
        setMessages([]); setActiveMsgId(null); setActiveMsg(null);
        if (activeMailbox === "__all__") {
            setActiveFolderId(null);
            return;
        }
        const list = foldersByMailbox[activeMailbox];
        if (list?.length) {
            const inbox = findPrimaryFolder(list);
            if (inbox) setActiveFolderId(inbox.id);
        }
    }, [activeMailbox, foldersByMailbox]);

    useEffect(() => { loadMessages(); }, [loadMessages]);

    // Ref to read the latest messages array without re-triggering the effect
    const messagesRef = useRef([]);
    useEffect(() => { messagesRef.current = messages; }, [messages]);

    useEffect(() => {
        if (activeMsgId) {
            const row = messagesRef.current.find((m) => m.id === activeMsgId);
            loadMessage(activeMsgId, row?._mailbox_id || null);
        }
    }, [activeMsgId, loadMessage]);

    // ----- Actions -----
    const markUnread = async (msgId, val) => {
        const mb = activeMsg?._mailbox_id || activeMailbox;
        try {
            await api.patch(`/ms/mail/messages/${msgId}?mailbox=${encodeURIComponent(mb)}`, { is_read: !val });
            setMessages((prev) => prev.map((p) => p.id === msgId ? { ...p, isRead: !val } : p));
            if (activeMsg?.id === msgId) setActiveMsg((m) => m ? { ...m, isRead: !val } : m);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const toggleFlag = async (msgId, currentStatus, sourceMb) => {
        const mb = sourceMb || activeMsg?._mailbox_id || activeMailbox;
        if (!mb || mb === "__all__") return;
        const next = currentStatus === "flagged" ? "notFlagged" : "flagged";
        try {
            await api.patch(`/ms/mail/messages/${msgId}?mailbox=${encodeURIComponent(mb)}`, { flag_status: next });
            setMessages((prev) => prev.map((p) => p.id === msgId ? { ...p, flag: { ...(p.flag || {}), flagStatus: next } } : p));
            if (activeMsg?.id === msgId) setActiveMsg((m) => m ? { ...m, flag: { ...(m.flag || {}), flagStatus: next } } : m);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const markComplete = async (msgId, sourceMb) => {
        const mb = sourceMb || activeMsg?._mailbox_id || activeMailbox;
        if (!mb || mb === "__all__") return;
        try {
            await api.patch(`/ms/mail/messages/${msgId}?mailbox=${encodeURIComponent(mb)}`, { flag_status: "complete" });
            setMessages((prev) => prev.map((p) => p.id === msgId ? { ...p, flag: { ...(p.flag || {}), flagStatus: "complete" } } : p));
            if (activeMsg?.id === msgId) setActiveMsg((m) => m ? { ...m, flag: { ...(m.flag || {}), flagStatus: "complete" } } : m);
            toast.success(t("Erledigt", "Completed"));
        } catch (e) { toast.error(errMsg(e)); }
    };

    const moveToFolder = async (msgId, destId) => {
        const mb = activeMsg?._mailbox_id || activeMailbox;
        try {
            await api.post(`/ms/mail/messages/${msgId}/move?mailbox=${encodeURIComponent(mb)}`, { destination_id: destId });
            toast.success(t("Verschoben", "Moved"));
            setMessages((prev) => prev.filter((p) => p.id !== msgId));
            if (activeMsgId === msgId) { setActiveMsgId(null); setActiveMsg(null); }
        } catch (e) { toast.error(errMsg(e)); }
    };

    const deleteMail = async (msgId) => {
        if (!window.confirm(t("Mail in den Papierkorb verschieben?", "Move to deleted items?"))) return;
        const mb = activeMsg?._mailbox_id || activeMailbox;
        try {
            await api.delete(`/ms/mail/messages/${msgId}?mailbox=${encodeURIComponent(mb)}`);
            setMessages((prev) => prev.filter((p) => p.id !== msgId));
            if (activeMsgId === msgId) { setActiveMsgId(null); setActiveMsg(null); }
        } catch (e) { toast.error(errMsg(e)); }
    };

    const doAiSummary = async () => {
        if (!activeMsg?.id) return;
        setAiBusy(true);
        try {
            const { data } = await api.post(`/ms/mail/messages/${activeMsg.id}/ai-summary?mailbox=${encodeURIComponent(activeMsg._mailbox_id)}`);
            setAiSummary(data.summary);
        } catch (e) { toast.error(errMsg(e)); }
        finally { setAiBusy(false); }
    };

    const startAiReply = async () => {
        if (!activeMsg?.id) return;
        setAiBusy(true);
        try {
            const { data } = await api.post(`/ms/mail/messages/${activeMsg.id}/ai-reply?mailbox=${encodeURIComponent(activeMsg._mailbox_id)}`, {});
            setComposeOpen({ kind: "reply", parent: activeMsg, html: data.draft_html, aiGenerated: true, mailbox: activeMsg._mailbox_id });
        } catch (e) { toast.error(errMsg(e)); }
        finally { setAiBusy(false); }
    };

    const createTaskFromMail = async () => {
        if (!activeMsg?.id) return;
        try {
            await api.post(`/ms/mail/messages/${activeMsg.id}/create-task?mailbox=${encodeURIComponent(activeMsg._mailbox_id)}`, {});
            toast.success(t("Task erstellt", "Task created"));
        } catch (e) { toast.error(errMsg(e)); }
    };

    const downloadAttachment = (att) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/ms/mail/messages/${activeMsg.id}/attachments/${att.id}/content?mailbox=${encodeURIComponent(activeMsg._mailbox_id)}`;
        window.open(url, "_blank");
    };

    const createFolder = async (mbId) => {
        const name = newFolderName.trim();
        if (!name) return;
        try {
            await api.post(`/ms/mail/folders?mailbox=${encodeURIComponent(mbId)}`, { display_name: name });
            toast.success(t("Ordner angelegt", "Folder created"));
            setNewFolderFor(null);
            setNewFolderName("");
            await loadFoldersFor(mbId);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const persistFolderOrder = useCallback(async (mbId, orderedFolders) => {
        try {
            await api.put("/ms/mail/folder-order", {
                mailbox: mbId,
                folder_ids: orderedFolders.map((f) => f.id),
            });
        } catch (_) { /* silent */ }
    }, []);

    const handleFolderDrop = (mbId, sourceIdx, targetIdx) => {
        if (sourceIdx === null || sourceIdx === targetIdx) { setDragKey(null); setDragOverKey(null); return; }
        const list = foldersByMailbox[mbId] || [];
        const next = [...list];
        const [moved] = next.splice(sourceIdx, 1);
        next.splice(targetIdx, 0, moved);
        setFoldersByMailbox((prev) => ({ ...prev, [mbId]: next }));
        persistFolderOrder(mbId, next);
        setDragKey(null); setDragOverKey(null);
    };

    const selectFolder = (mbId, folderId) => {
        setActiveMailbox(mbId);
        setActiveFolderId(folderId);
    };

    // ----- Empty state -----
    if (mailboxes.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-12 text-center" data-testid="mail-no-connection">
                <Mail className="w-12 h-12 text-neutral-300 mb-4" />
                <h2 className="font-display text-2xl font-bold mb-2">{t("Noch kein Postfach verbunden", "No mailbox connected yet")}</h2>
                <p className="text-sm text-neutral-500 mb-6 max-w-md">
                    {t("Bitte verbinde zuerst dein Microsoft-Konto unter Einstellungen → Microsoft 365.",
                       "Please connect your Microsoft account under Settings → Microsoft 365 first.")}
                </p>
                <Button onClick={() => window.location.href = "/settings"} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="mail-goto-settings">
                    {t("Zu den Einstellungen", "Go to settings")}
                </Button>
            </div>
        );
    }

    const activeMailboxObj = mailboxes.find((m) => m.id === activeMailbox);

    return (
        <div className="flex flex-col h-full bg-neutral-50" data-testid="mail-page">
            {/* Top toolbar */}
            <div className="bg-white border-b border-neutral-200 px-5 py-3 flex items-center gap-3 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate leading-tight">
                            {activeMailbox === "__all__" ? t("Alle Posteingänge", "All inboxes") : activeMailboxObj?.name || activeMailbox}
                        </p>
                        <p className="text-[10px] text-neutral-500 truncate leading-tight">
                            {activeMailbox === "__all__"
                                ? t(`${mailboxes.length} Postfächer vereint`, `${mailboxes.length} mailboxes`)
                                : activeMailboxObj?.email}
                        </p>
                    </div>
                </div>
                <div className="flex-1 relative max-w-xl ml-4">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-400 pointer-events-none" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") loadMessages(); }}
                        placeholder={t("E-Mails suchen…", "Search mails…")}
                        className="w-full h-9 pl-9 pr-3 text-sm bg-neutral-100 border border-transparent rounded-lg focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100 outline-none transition"
                        data-testid="mail-search-input"
                    />
                </div>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-neutral-600 hover:text-neutral-900">
                    <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="unread-only-toggle" />
                    {t("nur ungelesen", "unread only")}
                </label>
                <Button size="sm" onClick={loadMessages} variant="outline" disabled={loading} className="h-9 px-3" data-testid="mail-refresh" title={t("Aktualisieren", "Refresh")}>
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <Button
                    size="sm"
                    onClick={() => setComposeOpen({ kind: "new", mailbox: activeMailbox === "__all__" ? "me" : activeMailbox })}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4 gap-1.5"
                    data-testid="mail-compose"
                >
                    <Edit3 className="w-3.5 h-3.5" /> {t("Verfassen", "Compose")}
                </Button>
            </div>

            {/* 3-column body */}
            <div className="flex-1 flex min-h-0">
                {/* === Sidebar: Outlook-tree === */}
                <aside className="w-64 border-r border-neutral-200 overflow-y-auto bg-white flex-shrink-0 py-2" data-testid="folder-list">
                    {/* Unified inbox row */}
                    <button
                        onClick={() => setActiveMailbox("__all__")}
                        className={`w-full flex items-center gap-2.5 px-4 py-2 mb-2 text-sm transition border-l-2 ${
                            activeMailbox === "__all__"
                                ? "bg-blue-50 border-blue-500 text-blue-900 font-semibold"
                                : "border-transparent text-neutral-700 hover:bg-neutral-50"
                        }`}
                        data-testid="mailbox-all"
                    >
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Layers className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-semibold truncate leading-tight">{t("Alle Posteingänge", "All inboxes")}</p>
                            <p className="text-[10px] text-neutral-500 leading-tight">{t("Vereinte Ansicht", "Unified view")}</p>
                        </div>
                    </button>

                    {/* Each mailbox */}
                    {mailboxes.map((mb) => {
                        const expanded = expandedMailboxes[mb.id] !== false;
                        const mbFolders = foldersByMailbox[mb.id] || [];
                        const totalUnread = mbFolders.reduce((s, f) => s + (f.unreadItemCount || 0), 0);
                        return (
                            <div key={mb.id} className="mb-1.5">
                                <button
                                    onClick={() => setExpandedMailboxes((prev) => ({ ...prev, [mb.id]: !expanded }))}
                                    className="w-full flex items-center gap-2 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500 hover:bg-neutral-50 transition"
                                    data-testid={`mailbox-toggle-${mb.id}`}
                                >
                                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    <span
                                        className="w-5 h-5 rounded text-[10px] font-bold text-white flex items-center justify-center flex-shrink-0"
                                        style={{ background: avatarColor(mb.email || mb.id) }}
                                    >{initialsFor(mb.name || mb.email || "?")}</span>
                                    <span className="flex-1 truncate text-left text-neutral-700 normal-case">{mb.name}</span>
                                    {totalUnread > 0 && (
                                        <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full leading-none">{totalUnread}</span>
                                    )}
                                    {mb.type === "shared" && <span className="text-[9px] uppercase tracking-wider text-amber-600 normal-case">{t("geteilt", "shared")}</span>}
                                </button>
                                {expanded && (
                                    <div className="pl-2 space-y-0.5 mt-0.5">
                                        {mbFolders.length === 0 && (
                                            <p className="px-6 py-1 text-[10px] text-neutral-400 italic">{t("Lade Ordner…", "Loading folders…")}</p>
                                        )}
                                        {mbFolders.map((f, idx) => {
                                            const Icon = folderIconFor(f.displayName);
                                            const isActive = activeMailbox === mb.id && activeFolderId === f.id;
                                            const dKey = `${mb.id}::${idx}`;
                                            const isDragOver = dragOverKey === dKey && dragKey?.mbId === mb.id && dragKey.idx !== idx;
                                            return (
                                                <div
                                                    key={f.id}
                                                    draggable
                                                    onDragStart={() => setDragKey({ mbId: mb.id, idx })}
                                                    onDragOver={(e) => { e.preventDefault(); if (dragKey?.mbId === mb.id && dragOverKey !== dKey) setDragOverKey(dKey); }}
                                                    onDragLeave={() => { if (dragOverKey === dKey) setDragOverKey(null); }}
                                                    onDrop={(e) => { e.preventDefault(); if (dragKey?.mbId === mb.id) handleFolderDrop(mb.id, dragKey.idx, idx); }}
                                                    onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
                                                    className={isDragOver ? "border-t-2 border-t-blue-500" : ""}
                                                    data-testid={`folder-drag-${f.id}`}
                                                >
                                                    <button
                                                        onClick={() => selectFolder(mb.id, f.id)}
                                                        className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-sm rounded-md transition group ${
                                                            isActive
                                                                ? "bg-blue-100 text-blue-900 font-semibold"
                                                                : "text-neutral-700 hover:bg-neutral-100"
                                                        }`}
                                                        data-testid={`folder-${f.id}`}
                                                    >
                                                        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? "text-blue-700" : "text-neutral-500"}`} />
                                                        <span className="flex-1 truncate text-left">{f.displayName}</span>
                                                        {f.unreadItemCount > 0 && (
                                                            <span className={`text-[10px] ${isActive ? "text-blue-700" : "text-neutral-500"} font-semibold`}>{f.unreadItemCount}</span>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        {/* New folder button per mailbox */}
                                        {newFolderFor === mb.id ? (
                                            <div className="pl-7 pr-3 py-1 flex items-center gap-1">
                                                <input
                                                    autoFocus
                                                    value={newFolderName}
                                                    onChange={(e) => setNewFolderName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") createFolder(mb.id);
                                                        if (e.key === "Escape") { setNewFolderFor(null); setNewFolderName(""); }
                                                    }}
                                                    placeholder={t("Ordnername…", "Folder name…")}
                                                    className="flex-1 text-xs h-7 px-2 border border-blue-300 rounded outline-none focus:ring-2 focus:ring-blue-100"
                                                    data-testid={`new-folder-input-${mb.id}`}
                                                />
                                                <button onClick={() => createFolder(mb.id)} className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">✓</button>
                                                <button onClick={() => { setNewFolderFor(null); setNewFolderName(""); }} className="h-7 w-7 text-neutral-500 hover:text-neutral-900">✕</button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => { setNewFolderFor(mb.id); setNewFolderName(""); }}
                                                className="w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-xs text-neutral-400 hover:text-blue-600 hover:bg-blue-50/50 rounded-md transition"
                                                data-testid={`new-folder-${mb.id}`}
                                            >
                                                <FolderPlus className="w-3 h-3" />
                                                <span>{t("Neuer Ordner", "New folder")}</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </aside>

                {/* === Message list === */}
                <ul className="w-[420px] border-r border-neutral-200 overflow-y-auto flex-shrink-0 bg-white" data-testid="message-list">
                    {messages.length === 0 && !loading && (
                        <li className="p-10 text-center text-xs text-neutral-400">
                            <MailOpen className="w-10 h-10 text-neutral-200 mx-auto mb-2" />
                            {t("Keine Mails", "No mails")}
                        </li>
                    )}
                    {loading && messages.length === 0 && (
                        <li className="p-10 text-center text-xs text-neutral-400">
                            <Loader2 className="w-5 h-5 text-neutral-300 mx-auto animate-spin mb-2" />
                            {t("Lade Nachrichten…", "Loading messages…")}
                        </li>
                    )}
                    {messages.map((m) => {
                        const fromName = (m.from?.emailAddress?.name) || (m.from?.emailAddress?.address) || "?";
                        const fromAddr = m.from?.emailAddress?.address || "";
                        const flagged = m.flag?.flagStatus === "flagged";
                        const completed = m.flag?.flagStatus === "complete";
                        return (
                            <li
                                key={m.id + (m._mailbox_id || "")}
                                onClick={() => setActiveMsgId(m.id)}
                                className={`relative px-4 py-3 border-b border-neutral-100 cursor-pointer transition ${
                                    activeMsgId === m.id ? "bg-blue-50" : "hover:bg-neutral-50"
                                }`}
                                data-testid={`message-row-${m.id}`}
                            >
                                {!m.isRead && <span className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />}
                                <div className="flex items-start gap-3">
                                    {/* Avatar */}
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                                        style={{ background: avatarColor(fromAddr || fromName) }}
                                    >{initialsFor(fromName)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 mb-0.5">
                                            <p className={`flex-1 text-sm truncate ${!m.isRead ? "font-bold text-neutral-900" : "font-medium text-neutral-700"}`}>{fromName}</p>
                                            <span className="text-[10px] text-neutral-400 whitespace-nowrap flex-shrink-0">{fmtDateTime(m.receivedDateTime, lang)}</span>
                                        </div>
                                        <p className={`text-[13px] truncate mb-0.5 ${!m.isRead ? "font-semibold text-neutral-800" : "text-neutral-600"}`}>{m.subject || t("(ohne Betreff)", "(no subject)")}</p>
                                        <p className="text-[11px] text-neutral-400 truncate">{m.bodyPreview}</p>
                                        {/* Footer: badges */}
                                        <div className="flex items-center gap-1.5 mt-1.5">
                                            {activeMailbox === "__all__" && m._mailbox_name && (
                                                <span
                                                    className="text-[9px] px-1.5 py-0.5 rounded font-semibold text-white"
                                                    style={{ background: avatarColor(m._mailbox_id) }}
                                                >{m._mailbox_name}</span>
                                            )}
                                            {m.hasAttachments && <Paperclip className="w-3 h-3 text-neutral-400" />}
                                            {m.importance === "high" && <span className="text-[9px] text-red-600 font-bold uppercase">!</span>}
                                            {completed && (
                                                <span className="flex items-center gap-0.5 text-[9px] text-emerald-600 font-bold">
                                                    <CheckCircle2 className="w-3 h-3" /> {t("Erledigt", "Done")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Flag icon (clickable) */}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); toggleFlag(m.id, m.flag?.flagStatus, m._mailbox_id); }}
                                        className={`flex-shrink-0 mt-1 p-1 rounded hover:bg-neutral-200 ${flagged ? "text-red-500" : "text-neutral-300 hover:text-red-500"}`}
                                        title={flagged ? t("Markierung entfernen", "Remove flag") : t("Markieren", "Flag")}
                                        data-testid={`message-flag-${m.id}`}
                                    >
                                        <Flag className={`w-3.5 h-3.5 ${flagged ? "fill-red-500" : ""}`} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {/* === Message detail === */}
                <main className="flex-1 overflow-y-auto bg-white min-w-0">
                    {!activeMsg ? (
                        <div className="flex flex-col items-center justify-center h-full text-neutral-300">
                            <InboxIcon className="w-16 h-16 mb-2" />
                            <p className="text-sm">{t("Wähle eine E-Mail aus", "Select an email")}</p>
                        </div>
                    ) : (
                        <div className="max-w-4xl mx-auto p-6">
                            {/* Subject */}
                            <h1 className="font-display text-2xl font-bold text-neutral-900 mb-4 leading-tight">
                                {activeMsg.subject || t("(ohne Betreff)", "(no subject)")}
                            </h1>

                            {/* Sender card */}
                            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-neutral-100">
                                <div
                                    className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                    style={{ background: avatarColor(activeMsg.from?.emailAddress?.address || "") }}
                                >{initialsFor(activeMsg.from?.emailAddress?.name || "?")}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-semibold text-sm text-neutral-900">{activeMsg.from?.emailAddress?.name}</p>
                                        <span className="text-xs text-neutral-500 flex items-center gap-0.5"><AtSign className="w-3 h-3" />{activeMsg.from?.emailAddress?.address}</span>
                                        {activeMsg.flag?.flagStatus === "flagged" && (
                                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold flex items-center gap-1">
                                                <Flag className="w-3 h-3 fill-red-700" /> {t("Markiert", "Flagged")}
                                            </span>
                                        )}
                                        {activeMsg.flag?.flagStatus === "complete" && (
                                            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold flex items-center gap-1">
                                                <CheckCircle2 className="w-3 h-3" /> {t("Erledigt", "Completed")}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-0.5">
                                        <span className="text-neutral-400">{t("An:", "To:")}</span> {(activeMsg.toRecipients || []).map((r) => r.emailAddress?.address).join(", ")}
                                    </p>
                                    <p className="text-xs text-neutral-400 mt-0.5">{fmtFullDateTime(activeMsg.receivedDateTime, lang)}</p>
                                </div>
                            </div>

                            {/* Working-on badge: shows who has claimed this mail's task */}
                            {messageAssignee?.has_task && messageAssignee?.claimer && (
                                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-r-lg flex items-center gap-3" data-testid="mail-working-on-badge">
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: avatarColor(messageAssignee.claimer.email || "") }}>
                                        {initialsFor(messageAssignee.claimer.name || "?")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            {t("In Bearbeitung von:", "Being handled by:")} <span className="font-bold">{messageAssignee.claimer.name}</span>
                                            {messageAssignee.auto_assigned_by_pool && (
                                                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
                                                    <Sparkles className="w-2.5 h-2.5" /> {t("KI-Pool", "AI pool")}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[10px] text-emerald-700 mt-0.5">{messageAssignee.claimer.email} · Status: {messageAssignee.status}</p>
                                    </div>
                                </div>
                            )}
                            {messageAssignee?.has_task && !messageAssignee?.claimer && messageAssignee?.assigned_to_space && (
                                <div className="mb-4 p-2.5 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg text-xs text-amber-900 flex items-center gap-2" data-testid="mail-unclaimed-badge">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                                    {t("Noch nicht übernommen — bitte ein Teammitglied benachrichtigen.", "Not yet claimed — please notify a team member.")}
                                </div>
                            )}

                            {/* Primary actions */}
                            <div className="flex flex-wrap items-center gap-1.5 mb-5">
                                <Button size="sm" onClick={() => setComposeOpen({ kind: "reply", parent: activeMsg, mailbox: activeMsg._mailbox_id })} className="bg-blue-600 hover:bg-blue-700 text-white h-8 gap-1" data-testid="mail-reply">
                                    <Reply className="w-3.5 h-3.5" />{t("Antworten", "Reply")}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setComposeOpen({ kind: "replyAll", parent: activeMsg, mailbox: activeMsg._mailbox_id })} className="h-8 gap-1" data-testid="mail-reply-all">
                                    <ReplyAll className="w-3.5 h-3.5" />{t("Allen antworten", "Reply all")}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setComposeOpen({ kind: "forward", parent: activeMsg, mailbox: activeMsg._mailbox_id })} className="h-8 gap-1" data-testid="mail-forward">
                                    <Forward className="w-3.5 h-3.5" />{t("Weiterleiten", "Forward")}
                                </Button>
                                <span className="w-px h-5 bg-neutral-200 mx-1" />
                                <Button size="sm" variant="outline" onClick={startAiReply} disabled={aiBusy} className="h-8 gap-1" data-testid="mail-ai-reply">
                                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-amber-500" />}
                                    {t("KI-Antwort", "AI reply")}
                                </Button>
                                <Button size="sm" variant="outline" onClick={doAiSummary} disabled={aiBusy} className="h-8 gap-1" data-testid="mail-ai-summary">
                                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-blue-500" />}
                                    {t("Zusammenfassen", "Summarize")}
                                </Button>
                                <Button size="sm" variant="outline" onClick={createTaskFromMail} className="h-8 gap-1" data-testid="mail-create-task">
                                    <ListPlus className="w-3.5 h-3.5" />{t("Task erstellen", "Create task")}
                                </Button>
                                <span className="w-px h-5 bg-neutral-200 mx-1" />
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleFlag(activeMsg.id, activeMsg.flag?.flagStatus, activeMsg._mailbox_id)}
                                    className={`h-8 gap-1 ${activeMsg.flag?.flagStatus === "flagged" ? "text-red-600" : "text-neutral-600"}`}
                                    data-testid="mail-toggle-flag"
                                >
                                    <Flag className={`w-3.5 h-3.5 ${activeMsg.flag?.flagStatus === "flagged" ? "fill-red-600" : ""}`} />
                                    {activeMsg.flag?.flagStatus === "flagged" ? t("Markierung entfernen", "Unflag") : t("Markieren", "Flag")}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => markComplete(activeMsg.id, activeMsg._mailbox_id)}
                                    className="h-8 gap-1 text-emerald-600"
                                    data-testid="mail-mark-complete"
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5" />{t("Erledigt", "Done")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => markUnread(activeMsg.id, activeMsg.isRead)} className="h-8 gap-1 text-neutral-600" data-testid="mail-mark-unread">
                                    <MailOpen className="w-3.5 h-3.5" />
                                    {activeMsg.isRead ? t("Ungelesen", "Unread") : t("Gelesen", "Read")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteMail(activeMsg.id)} className="h-8 gap-1 text-red-500" data-testid="mail-delete">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                                <FolderMoveMenu folders={foldersByMailbox[activeMsg._mailbox_id] || folders} onMove={(fid) => moveToFolder(activeMsg.id, fid)} lang={lang} />
                            </div>

                            {/* AI summary */}
                            {aiSummary && (
                                <div className="mb-5 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl" data-testid="ai-summary-box">
                                    <p className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> {t("KI-Zusammenfassung", "AI summary")}</p>
                                    <pre className="text-xs whitespace-pre-wrap font-sans text-blue-900 leading-relaxed">{aiSummary}</pre>
                                </div>
                            )}

                            {/* Attachments */}
                            {attachments.length > 0 && (
                                <div className="mb-5 p-4 bg-neutral-50 border border-neutral-200 rounded-xl" data-testid="attachments-box">
                                    <p className="text-xs font-semibold mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {attachments.length} {t("Anhänge", "Attachments")}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {attachments.map((a) => (
                                            <button key={a.id} onClick={() => downloadAttachment(a)} className="text-xs bg-white border border-neutral-200 rounded-lg px-3 py-1.5 flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 transition" data-testid={`att-${a.id}`}>
                                                <Download className="w-3 h-3 text-blue-600" /> {a.name}
                                                <span className="text-neutral-400 text-[10px]">({Math.round((a.size || 0) / 1024)} KB)</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Body */}
                            <div
                                className="prose prose-sm max-w-none mail-body leading-relaxed text-neutral-800"
                                data-testid="mail-body"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(activeMsg.body?.content || "", {
                                        USE_PROFILES: { html: true },
                                        ADD_TAGS: ["style"],
                                        ADD_ATTR: ["target"],
                                    })
                                }}
                            />
                        </div>
                    )}
                </main>
            </div>

            {composeOpen && (
                <ComposeDialog
                    composeOpen={composeOpen}
                    onClose={() => setComposeOpen(null)}
                    activeMailbox={composeOpen.mailbox || (activeMailbox === "__all__" ? "me" : activeMailbox)}
                    lang={lang}
                    onSent={() => { setComposeOpen(null); toast.success(t("Gesendet", "Sent")); loadMessages(); }}
                />
            )}
        </div>
    );
}

function FolderMoveMenu({ folders, onMove, lang }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} className="h-8 gap-1 text-neutral-600" data-testid="mail-move-menu">
                <Folder className="w-3.5 h-3.5" />{lang === "de" ? "Verschieben" : "Move"}<ChevronDown className="w-3 h-3" />
            </Button>
            {open && (
                <div className="absolute z-10 right-0 mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto">
                    {folders.map((f) => (
                        <button key={f.id} onClick={() => { onMove(f.id); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-2">
                            <Folder className="w-3 h-3 text-neutral-400" />{f.displayName}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function ComposeDialog({ composeOpen, onClose, activeMailbox, lang, onSent }) {
    const t = (de, en) => (lang === "de" ? de : en);
    const parent = composeOpen.parent;
    const isReply = composeOpen.kind === "reply" || composeOpen.kind === "replyAll";
    const isForward = composeOpen.kind === "forward";
    const isNew = composeOpen.kind === "new";
    // If the active mailbox looks like a shared mailbox (email address, not "me"),
    // backend will auto-route the send through the user's personal mailbox and
    // auto-CC the shared address.
    const isSharedMailbox = activeMailbox && activeMailbox !== "me" && activeMailbox.includes("@");

    const defaultTo = useMemo(() => {
        if (isReply && parent) {
            const addrs = composeOpen.kind === "replyAll"
                ? [parent.from?.emailAddress?.address, ...(parent.toRecipients || []).map((r) => r.emailAddress?.address)].filter(Boolean)
                : [parent.from?.emailAddress?.address].filter(Boolean);
            return [...new Set(addrs)].join(", ");
        }
        return "";
    }, [isReply, parent, composeOpen.kind]);

    // Quote original message into the body for reply/forward — like Outlook does.
    const quotedBody = useMemo(() => {
        if (!parent || !(isReply || isForward) || composeOpen.html) return "";
        const fromName = parent.from?.emailAddress?.name || "";
        const fromAddr = parent.from?.emailAddress?.address || "";
        const date = parent.receivedDateTime
            ? new Date(parent.receivedDateTime).toLocaleString(lang === "de" ? "de-DE" : "en-US", { dateStyle: "full", timeStyle: "short" })
            : "";
        const toList = (parent.toRecipients || []).map((r) => r.emailAddress?.address).filter(Boolean).join(", ");
        const hdr = (lang === "de"
            ? `<b>Von:</b> ${fromName} &lt;${fromAddr}&gt;<br><b>Gesendet:</b> ${date}<br><b>An:</b> ${toList}<br><b>Betreff:</b> ${parent.subject || ""}`
            : `<b>From:</b> ${fromName} &lt;${fromAddr}&gt;<br><b>Sent:</b> ${date}<br><b>To:</b> ${toList}<br><b>Subject:</b> ${parent.subject || ""}`
        );
        return `<p><br></p><p><br></p><hr style="border:0;border-top:1px solid #d4d4d8;margin:18px 0"><div style="color:#52525b;font-size:13px;margin-bottom:10px;line-height:1.5">${hdr}</div><blockquote style="border-left:3px solid #e4e4e7;padding-left:14px;color:#3f3f46">${(parent.body?.content || "").trim()}</blockquote>`;
    }, [parent, isReply, isForward, composeOpen.html, lang]);

    const [to, setTo] = useState(defaultTo);
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [showBcc, setShowBcc] = useState(false);
    const [subject, setSubject] = useState(isReply && parent ? `Re: ${parent.subject || ""}` : isForward && parent ? `Fw: ${parent.subject || ""}` : "");
    const [bodyHtml, setBodyHtml] = useState((composeOpen.html || "") + quotedBody);
    // Send-as choice for shared mailboxes: "personal_cc" (default) sends from user's mailbox with shared in CC;
    // "shared" sends directly from the shared mailbox (no auto-CC) — useful for impersonal team replies.
    const [sendAs, setSendAs] = useState(isSharedMailbox ? "personal_cc" : "personal");
    const [attachments, setAttachments] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [signature, setSignature] = useState({ html: "", enabled: true });
    const [appendSig, setAppendSig] = useState(true);
    const [showSigEdit, setShowSigEdit] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [tpl, sig] = await Promise.all([
                    api.get("/ms/mail/templates"),
                    api.get("/ms/mail/signature"),
                ]);
                setTemplates(tpl.data || []);
                setSignature(sig.data || { html: "", enabled: true });
                setAppendSig((sig.data?.enabled !== false) && !!sig.data?.html);
            } catch (_) { /* ignore */ }
        })();
    }, []);

    const insertTemplate = (tplId) => {
        const tpl = templates.find((tp) => tp.id === tplId);
        if (!tpl) return;
        if (tpl.subject && (isNew || !subject)) setSubject(tpl.subject);
        setBodyHtml((prev) => (prev ? prev + "<br>" : "") + tpl.body_html);
        toast.success(t("Vorlage eingefügt", "Template inserted"));
    };

    const saveAsTemplate = async () => {
        const name = window.prompt(t("Name der Vorlage:", "Template name:"));
        if (!name) return;
        try {
            await api.post("/ms/mail/templates", { name, subject, body_html: bodyHtml });
            const { data } = await api.get("/ms/mail/templates");
            setTemplates(data || []);
            toast.success(t("Vorlage gespeichert", "Template saved"));
        } catch (e) { toast.error(errMsg(e)); }
    };

    const saveSignature = async () => {
        try {
            await api.put("/ms/mail/signature", { html: signature.html, enabled: signature.enabled });
            toast.success(t("Signatur gespeichert", "Signature saved"));
            setShowSigEdit(false);
            setAppendSig(signature.enabled && !!signature.html);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const addAttachment = async (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) {
            return toast.error(`${file.name}: ${t("zu groß (max. 4 MB)", "too large (max 4 MB)")}`);
        }
        const reader = new FileReader();
        reader.onload = () => {
            const b64 = String(reader.result).split(",")[1];
            setAttachments((prev) => [...prev, { name: file.name, content_type: file.type || "application/octet-stream", content_b64: b64, size: file.size }]);
        };
        reader.readAsDataURL(file);
    };

    const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

    const send = async () => {
        if ((isNew || isForward) && !to.trim()) { toast.error(t("Empfänger fehlt", "Recipient required")); return; }
        setBusy(true);
        const toList = to.split(",").map((s) => s.trim()).filter(Boolean);
        const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
        const bccList = bcc.split(",").map((s) => s.trim()).filter(Boolean);
        const attsPayload = attachments.map(({ name, content_type, content_b64 }) => ({ name, content_type, content_b64 }));
        // When user explicitly picks "from shared", we bypass the auto-routing
        // by setting send_as_shared=true (handled in backend).
        const sendAsShared = sendAs === "shared";
        try {
            if (isReply && parent) {
                await api.post(`/ms/mail/messages/${parent.id}/reply?mailbox=${encodeURIComponent(activeMailbox)}`, {
                    body_html: bodyHtml,
                    reply_all: composeOpen.kind === "replyAll",
                    attachments: attsPayload.length ? attsPayload : undefined,
                    append_signature: appendSig,
                    send_as_shared: sendAsShared,
                });
            } else if (isForward && parent) {
                await api.post(`/ms/mail/messages/${parent.id}/forward?mailbox=${encodeURIComponent(activeMailbox)}`, {
                    to_recipients: toList,
                    comment: bodyHtml,
                    attachments: attsPayload.length ? attsPayload : undefined,
                    append_signature: appendSig,
                    send_as_shared: sendAsShared,
                });
            } else {
                await api.post(`/ms/mail/send?mailbox=${encodeURIComponent(activeMailbox)}`, {
                    to: toList,
                    cc: ccList.length ? ccList : undefined,
                    bcc: bccList.length ? bccList : undefined,
                    subject,
                    body_html: bodyHtml,
                    attachments: attsPayload.length ? attsPayload : undefined,
                    append_signature: appendSig,
                    send_as_shared: sendAsShared,
                });
            }
            onSent?.();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    // Display sender for "Von:" field
    const userEmail = isSharedMailbox
        ? null  // server will route via personal — show "Auto-routing" hint
        : activeMailbox === "me"
            ? (composeOpen.fromEmail || "Mein Postfach")
            : activeMailbox;

    return (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-2 md:p-6" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
                style={{ width: "min(96vw, 1400px)", height: "min(92vh, 920px)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="compose-dialog"
            >
                {/* === Outlook-style title bar === */}
                <div className="h-9 px-3 flex items-center justify-between bg-neutral-100 border-b border-neutral-200 flex-shrink-0">
                    <div className="flex items-center gap-2 text-xs font-medium text-neutral-700">
                        <Edit3 className="w-3.5 h-3.5 text-blue-600" />
                        <span>
                            {isNew ? t("Neue E-Mail", "New email") :
                             isReply ? (composeOpen.kind === "replyAll" ? t("Allen antworten", "Reply all") : t("Antworten", "Reply")) :
                             t("Weiterleiten", "Forward")}
                        </span>
                        {composeOpen.aiGenerated && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">KI-Entwurf</span>}
                    </div>
                    <div className="flex items-center gap-0.5">
                        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center hover:bg-red-500 hover:text-white rounded transition" title={t("Schließen", "Close")} data-testid="compose-close">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>

                {/* === Shared mailbox hint (thin amber strip) === */}
                {isSharedMailbox && (
                    <div className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 flex items-center gap-2 text-[11px] text-amber-900 flex-shrink-0" data-testid="compose-shared-hint">
                        <Sparkles className="w-3 h-3 text-amber-600 flex-shrink-0" />
                        <span>
                            {t("Wird von deinem persönlichen Konto gesendet mit ", "Sent from your personal account with ")}
                            <span className="font-mono font-bold">{activeMailbox}</span>
                            {t(" automatisch in CC.", " auto-added to CC.")}
                        </span>
                    </div>
                )}

                {/* === Quick toolbar (Outlook-style: Send + From) === */}
                <div className="h-12 px-3 flex items-center gap-3 border-b border-neutral-200 bg-white flex-shrink-0">
                    <Button
                        onClick={send}
                        disabled={busy}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-2 h-9 px-4 rounded-md shadow-sm"
                        data-testid="compose-send"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
                        <span className="font-medium">{t("Senden", "Send")}</span>
                    </Button>
                    {isSharedMailbox ? (
                        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-neutral-50">
                            <span className="text-xs text-neutral-400">{t("Von:", "From:")}</span>
                            <select
                                value={sendAs}
                                onChange={(e) => setSendAs(e.target.value)}
                                className="text-xs font-semibold text-neutral-800 bg-transparent border border-neutral-200 hover:border-blue-400 rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-100"
                                data-testid="compose-send-as"
                            >
                                <option value="personal_cc">{t(`Mein Konto (mit ${activeMailbox} in CC)`, `My account (with ${activeMailbox} in CC)`)}</option>
                                <option value="shared">{t(`${activeMailbox} (direkt vom freigegebenen Postfach)`, `${activeMailbox} (directly from shared mailbox)`)}</option>
                            </select>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 text-xs text-neutral-600 px-2 py-1.5 rounded">
                            <span className="text-neutral-400">{t("Von:", "From:")}</span>
                            <span className="font-semibold text-neutral-800">{userEmail || t("mein Postfach", "my mailbox")}</span>
                        </div>
                    )}
                    <div className="flex-1" />
                    {templates.length > 0 && (
                        <select onChange={(e) => { if (e.target.value) { insertTemplate(e.target.value); e.target.value = ""; } }} className="text-xs h-8 border border-neutral-200 rounded px-2 bg-neutral-50 hover:bg-white" data-testid="compose-templates" defaultValue="">
                            <option value="" disabled>{t("Vorlage einfügen…", "Insert template…")}</option>
                            {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                        </select>
                    )}
                    <Button size="sm" variant="ghost" onClick={saveAsTemplate} className="h-8 gap-1" title={t("Als Vorlage speichern", "Save as template")} data-testid="compose-save-template">
                        <Plus className="w-3.5 h-3.5" /> <span className="text-xs">{t("Vorlage speichern", "Save template")}</span>
                    </Button>
                </div>

                {/* === Recipient fields (Outlook-style) === */}
                <div className="px-3 pt-2 border-b border-neutral-200 flex-shrink-0 space-y-0">
                    {/* An / To */}
                    <div className="flex items-baseline border-b border-neutral-100 hover:border-blue-200 transition group">
                        <div className="w-12 flex-shrink-0 py-2">
                            <span className="text-xs font-semibold text-neutral-700 px-2 py-1 rounded bg-neutral-100 group-focus-within:bg-blue-50 group-focus-within:text-blue-700">
                                {t("An", "To")}
                            </span>
                        </div>
                        <input
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder={t("Empfänger eingeben…", "Type recipients…")}
                            className="flex-1 h-10 px-2 text-sm bg-transparent outline-none"
                            data-testid="compose-to"
                        />
                        {!showBcc && (
                            <button
                                onClick={() => setShowBcc(true)}
                                className="text-xs text-neutral-400 hover:text-blue-600 px-2 flex-shrink-0"
                                data-testid="compose-add-bcc"
                            >Bcc</button>
                        )}
                    </div>
                    {/* Cc */}
                    <div className="flex items-baseline border-b border-neutral-100 hover:border-blue-200 transition group">
                        <div className="w-12 flex-shrink-0 py-2">
                            <span className="text-xs font-semibold text-neutral-700 px-2 py-1 rounded bg-neutral-100 group-focus-within:bg-blue-50 group-focus-within:text-blue-700">Cc</span>
                        </div>
                        <input
                            value={cc}
                            onChange={(e) => setCc(e.target.value)}
                            placeholder={t("Cc (optional)", "Cc (optional)")}
                            className="flex-1 h-10 px-2 text-sm bg-transparent outline-none"
                            data-testid="compose-cc"
                        />
                    </div>
                    {/* Bcc (collapsed by default) */}
                    {showBcc && (
                        <div className="flex items-baseline border-b border-neutral-100 hover:border-blue-200 transition group">
                            <div className="w-12 flex-shrink-0 py-2">
                                <span className="text-xs font-semibold text-neutral-700 px-2 py-1 rounded bg-neutral-100 group-focus-within:bg-blue-50 group-focus-within:text-blue-700">Bcc</span>
                            </div>
                            <input
                                value={bcc}
                                onChange={(e) => setBcc(e.target.value)}
                                placeholder={t("Bcc (verborgen)", "Bcc (hidden)")}
                                className="flex-1 h-10 px-2 text-sm bg-transparent outline-none"
                                data-testid="compose-bcc"
                            />
                            <button onClick={() => { setShowBcc(false); setBcc(""); }} className="text-xs text-neutral-400 hover:text-red-500 px-2"><X className="w-3 h-3 inline" /></button>
                        </div>
                    )}
                    {/* Subject */}
                    <div className="flex items-center">
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder={t("Betreff hinzufügen", "Add subject")}
                            className="flex-1 h-11 px-2 text-sm font-medium bg-transparent outline-none"
                            data-testid="compose-subject"
                            disabled={isReply}
                        />
                        <span className="text-[10px] text-neutral-400 px-2 flex-shrink-0" data-testid="compose-autosave">
                            {bodyHtml ? t("Entwurf bereit", "Draft ready") : ""}
                        </span>
                    </div>
                </div>

                {/* === Body editor (big Outlook-style area) === */}
                <div className="flex-1 flex flex-col overflow-hidden bg-white min-h-0 compose-body" data-testid="compose-rich-editor" style={{ minHeight: 400 }}>
                    <NoteRichEditor
                        value={bodyHtml}
                        onChange={({ html }) => setBodyHtml(html)}
                        lang={lang}
                    />
                </div>

                {/* === Attachments + signature footer === */}
                <div className="border-t border-neutral-200 flex-shrink-0 bg-neutral-50">
                    {attachments.length > 0 && (
                        <div className="px-3 py-2 border-b border-neutral-100" data-testid="compose-attachments">
                            <div className="flex items-center gap-2 mb-1.5">
                                <Paperclip className="w-3 h-3 text-neutral-500" />
                                <span className="text-xs font-semibold text-neutral-700">{attachments.length} {t("Anhang", "Attachment")}{attachments.length > 1 ? (lang === "de" ? "e" : "s") : ""}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {attachments.map((a, i) => (
                                    <span key={i} className="text-xs bg-white border border-neutral-200 rounded px-2.5 py-1 flex items-center gap-1.5" data-testid={`compose-att-${i}`}>
                                        <Paperclip className="w-3 h-3 text-blue-600" />{a.name} <span className="text-neutral-400">({Math.round((a.size || 0) / 1024)} KB)</span>
                                        <button onClick={() => removeAttachment(i)} className="hover:text-red-500 ml-1"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="px-3 py-2">
                        <DropZone
                            accept="*/*"
                            multiple
                            onFile={addAttachment}
                            compact
                            label={t("Anhänge: ziehen, Strg+V oder klicken (max. 4 MB)", "Attach: drop, Ctrl+V or click (max 4 MB)")}
                            testId="compose-dropzone"
                        />
                    </div>
                    <div className="px-3 py-2 flex items-center justify-between gap-2 border-t border-neutral-100">
                        <div className="flex items-center gap-3 text-xs">
                            <label className="flex items-center gap-1 cursor-pointer text-neutral-600">
                                <input type="checkbox" checked={appendSig} onChange={(e) => setAppendSig(e.target.checked)} data-testid="compose-toggle-sig" />
                                {t("Signatur anhängen", "Append signature")}
                            </label>
                            <button onClick={() => setShowSigEdit(true)} className="text-blue-600 hover:underline flex items-center gap-1" data-testid="compose-edit-sig">
                                <Pen className="w-3 h-3" /> {t("Signatur bearbeiten", "Edit signature")}
                            </button>
                        </div>
                        <Button variant="outline" size="sm" onClick={onClose} className="h-7 text-xs" data-testid="compose-cancel">{t("Verwerfen", "Discard")}</Button>
                    </div>
                </div>
            </div>

            {showSigEdit && (
                <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setShowSigEdit(false)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="signature-editor">
                        <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                            <p className="font-semibold flex items-center gap-2"><FileSignature className="w-4 h-4" /> {t("E-Mail-Signatur", "Email signature")}</p>
                            <button onClick={() => setShowSigEdit(false)}><X className="w-4 h-4" /></button>
                        </header>
                        <div className="p-4 space-y-2">
                            <label className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={signature.enabled} onChange={(e) => setSignature({ ...signature, enabled: e.target.checked })} />
                                {t("Signatur aktiviert", "Signature enabled")}
                            </label>
                            <textarea
                                value={signature.html}
                                onChange={(e) => setSignature({ ...signature, html: e.target.value })}
                                rows={8}
                                className="w-full border border-neutral-200 rounded-lg p-3 text-sm font-mono"
                                placeholder={t("HTML-Signatur (Mit freundlichen Grüßen<br>Dein Name<br>...)", "HTML signature")}
                                data-testid="signature-input"
                            />
                            <p className="text-[10px] text-neutral-500">{t("HTML erlaubt. Wird automatisch unter jede Mail gehängt, sofern 'Signatur anhängen' aktiv ist.", "HTML allowed. Auto-appended when 'Append signature' is on.")}</p>
                        </div>
                        <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowSigEdit(false)}>{t("Abbrechen", "Cancel")}</Button>
                            <Button onClick={saveSignature} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="signature-save">{t("Speichern", "Save")}</Button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
